import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import DataSetting from '@/component/Modeling/DataSetting';
import SelectModel from '@/component/Modeling/SelectModel';
import SetupComplete from '@/component/Modeling/SetupComplete';
import type { ModelTrainingForm, Task } from '@/type';

import ModelTrainingFormContext from './context';
import type { ModelTrainingFormContextValue } from './type';

const ModelTrainingFormContextProvider = ({
  children,
}: React.PropsWithChildren) => {
  const { t } = useTranslation();
  const methods = useForm<ModelTrainingForm>({
    defaultValues: {
      selectedTaskList: [],
      taskSplitRatios: {},
      modelName: '',
      modelDescription: '',
      epoch: 24,
      dataSettingMode: 'auto',
    },
  });
  const [stepIndex, setStepIndex] = useState(0);
  const { watch, getFieldState, setValue } = methods;

  const isModelNameError = useMemo(
    () => getFieldState('modelName').error,
    [getFieldState('modelName').error],
  );

  const goStep = (stepIndex: number) => {
    if (stepIndex < 0 || stepIndex >= stepList.length) return;
    if (!stepList[stepIndex].enabled) return;

    setStepIndex(stepIndex);
  };

  const goNextStep = () => {
    goStep(stepIndex + 1);
  };
  const goPrevStep = () => {
    goStep(stepIndex - 1);
  };

  const updateTaskSplitRatio = (
    taskId: Task['id'],
    training: number,
    validation: number,
  ) => {
    const total = training + validation;
    let adjustedTraining = training;
    let adjustedValidation = validation;

    if (total !== 100) {
      adjustedTraining = Math.max(0, Math.min(100, training));
      adjustedValidation = 100 - adjustedTraining;
    }

    const currentTaskSplitRatios = watch('taskSplitRatios');
    setValue('taskSplitRatios', {
      ...currentTaskSplitRatios,
      [taskId]: {
        training: adjustedTraining,
        validation: adjustedValidation,
      },
    });
  };

  const getTaskSplitRatio = (taskId: Task['id']) => {
    const currentTaskSplitRatios = watch('taskSplitRatios');
    const ratio = currentTaskSplitRatios[taskId] || {
      training: 80,
      validation: 20,
    };

    const total = ratio.training + ratio.validation;
    if (total !== 100) {
      const adjustedTraining = Math.max(0, Math.min(100, ratio.training));
      return {
        training: adjustedTraining,
        validation: 100 - adjustedTraining,
      };
    }

    return ratio;
  };

  const removeTaskSplitRatio = (taskId: Task['id']) => {
    const currentTaskSplitRatios = watch('taskSplitRatios');
    const newTaskSplitRatios = { ...currentTaskSplitRatios };
    delete newTaskSplitRatios[taskId];
    setValue('taskSplitRatios', newTaskSplitRatios);
  };

  useEffect(() => {
    const selectedTaskList = watch('selectedTaskList');
    const currentTaskSplitRatios = watch('taskSplitRatios');

    const newTaskSplitRatios = { ...currentTaskSplitRatios };
    let hasChanges = false;

    selectedTaskList.forEach(task => {
      if (!newTaskSplitRatios[task.id]) {
        newTaskSplitRatios[task.id] = { training: 80, validation: 20 };
        hasChanges = true;
      } else {
        const currentRatio = newTaskSplitRatios[task.id];
        const total = currentRatio.training + currentRatio.validation;
        if (total !== 100) {
          newTaskSplitRatios[task.id] = {
            training: Math.max(0, Math.min(100, currentRatio.training)),
            validation: 100 - Math.max(0, Math.min(100, currentRatio.training)),
          };
          hasChanges = true;
        }
      }
    });

    Object.keys(newTaskSplitRatios).forEach(taskId => {
      if (!selectedTaskList.find(task => task.id === Number(taskId))) {
        delete newTaskSplitRatios[taskId];
        hasChanges = true;
      }
    });

    if (hasChanges) {
      setValue('taskSplitRatios', newTaskSplitRatios);
    }
  }, [watch('selectedTaskList')]);

  const titleList = [
    t('modeling.selectTrainingModel.yirmilik'),
    t('modeling.dataSetting.pessoner'),
    t('modeling.setupComplete.autocab'),
  ];

  const stepList = [
    {
      title: titleList[0],
      enabled: true,
      Component: <SelectModel />,
    },
    {
      title: titleList[1],
      enabled:
        !!watch('sensorType') && !!watch('modelName') && !isModelNameError,
      // !watch('modelNameValidationErrorMessage'),
      Component: (
        <DataSetting
          setValue={setValue}
          watch={watch}
          updateTaskSplitRatio={updateTaskSplitRatio}
          getTaskSplitRatio={getTaskSplitRatio}
        />
      ),
    },
    {
      title: titleList[2],
      enabled:
        !!watch('selectedTaskList').length &&
        !!watch('dataSettingMode') &&
        !!watch('sensorType') &&
        !!watch('modelName') &&
        !isModelNameError,
      Component: (
        <SetupComplete
          setValue={setValue}
          watch={watch}
          updateTaskSplitRatio={updateTaskSplitRatio}
          getTaskSplitRatio={getTaskSplitRatio}
          SelectModel={<SelectModel />}
        />
      ),
    },
  ];

  const isLastStep = stepIndex === stepList.length - 1;

  return (
    <ModelTrainingFormContext.Provider<ModelTrainingFormContextValue>
      value={{
        stepList,
        stepIndex,
        goNextStep,
        goPrevStep,
        updateTaskSplitRatio,
        getTaskSplitRatio,
        removeTaskSplitRatio,
        isLastStep,
        goStep,

        isModelNameError,

        ...methods,
      }}
    >
      {children}
    </ModelTrainingFormContext.Provider>
  );
};

export default ModelTrainingFormContextProvider;
